import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Button, Modal, Form, Input, InputNumber, Select, message, Descriptions, Table, Space, Popconfirm, Popover, TimePicker, Switch, Tooltip, Alert, Upload, Avatar } from 'antd';
import { EditOutlined, PlusOutlined, DeleteOutlined, InfoCircleOutlined, CopyOutlined, SyncOutlined, UploadOutlined, LoadingOutlined, TeamOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';
import dayjs from 'dayjs';
import OrganizerHero from './OrganizerHero';

const { Title } = Typography;
const { Option } = Select;

const TeamConfig = () => {
  const [teamConfig, setTeamConfig] = useState(null);
  const [gameSettings, setGameSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gameSettingsLoading, setGameSettingsLoading] = useState(false);
  const [features, setFeatures] = useState({ stats_enabled: false });
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [gameSettingModalVisible, setGameSettingModalVisible] = useState(false);
  const [editingGameSetting, setEditingGameSetting] = useState(null);
  const [form] = Form.useForm();
  const [gameSettingForm] = Form.useForm();
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  const fetchTeamConfig = async () => {
    setLoading(true);
    try {
      const response = await api.get('/team-config');
      setTeamConfig(response.data);
    } catch (error) {
      message.error('Erro ao carregar configuração do time');
    }
    setLoading(false);
  };

  const fetchGameSettings = async () => {
    setGameSettingsLoading(true);
    try {
      const response = await api.get('/organizer-game-settings');
      setGameSettings(response.data);
    } catch (error) {
      message.error('Erro ao carregar dias de jogos');
    }
    setGameSettingsLoading(false);
  };

  const fetchFeatures = async () => {
    try {
      const r = await api.get('/organizer/features');
      setFeatures({ stats_enabled: !!r.data?.stats_enabled });
    } catch (error) {
      // silencioso — feature opcional
    }
  };

  const toggleStats = async (checked) => {
    setFeaturesLoading(true);
    try {
      const r = await api.patch('/organizer/features', { stats_enabled: checked });
      setFeatures({ stats_enabled: !!r.data?.stats_enabled });
      message.success(checked ? 'Estatísticas ativadas' : 'Estatísticas desativadas');
    } catch (error) {
      message.error('Erro ao atualizar configuração');
    }
    setFeaturesLoading(false);
  };

  useEffect(() => {
    fetchTeamConfig();
    fetchGameSettings();
    fetchFeatures();
  }, []);

  const navigate = useNavigate();

  const handleResync = () => {
    if (!teamConfig?.groupjid) {
      navigate('/sincronizar');
      return;
    }
    Modal.confirm({
      title: 'Ressincronizar grupo do WhatsApp',
      content: 'Tem certeza? As próximas mensagens deixarão de ir para o grupo atual até você concluir a nova sincronização.',
      okText: 'Sim, ressincronizar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await api.post('/organizer/resync-group');
          message.success('Ressincronização iniciada. Siga as instruções para concluir.');
          navigate('/sincronizar');
        } catch (e) {
          message.error('Erro ao ressincronizar grupo');
        }
      },
    });
  };

  const showModal = () => {
    setModalVisible(true);
    if (teamConfig) {
      const formData = { ...teamConfig };
      if (formData.list_description) {
        formData.list_description = formData.list_description.replace(/\\n/g, '\n');
      }
      form.setFieldsValue(formData);
      setLogoUrl(teamConfig.logo_url || null);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const response = await api.put('/team-config', values);
      setTeamConfig(response.data);
      message.success('Configuração atualizada com sucesso!');
      setModalVisible(false);
    } catch (error) {
      message.error('Erro ao atualizar configuração');
    }
  };

  const showGameSettingModal = (gameSetting = null) => {
    setEditingGameSetting(gameSetting);
    setGameSettingModalVisible(true);
    if (gameSetting) {
      const formData = { ...gameSetting };
      if (formData.time) formData.time = dayjs(formData.time, 'HH:mm');
      if (formData.early_time_at) formData.early_time_at = dayjs(formData.early_time_at, 'HH:mm');
      if (formData.first_time_at) formData.first_time_at = dayjs(formData.first_time_at, 'HH:mm');
      if (formData.second_time_at) formData.second_time_at = dayjs(formData.second_time_at, 'HH:mm');
      if (formData.third_time_at) formData.third_time_at = dayjs(formData.third_time_at, 'HH:mm');
      gameSettingForm.setFieldsValue(formData);
    } else {
      gameSettingForm.resetFields();
    }
  };

  const doGameSettingSave = async (formData) => {
    if (editingGameSetting) {
      const r = await api.put(`/organizer-game-settings/${editingGameSetting.id}`, formData);
      const n = r?.data?.games_recomputed || 0;
      message.success(n > 0
        ? `Dia de jogo atualizado! ${n} jogo${n > 1 ? 's' : ''} aberto${n > 1 ? 's' : ''} sincronizado${n > 1 ? 's' : ''} com a nova config.`
        : 'Dia de jogo atualizado com sucesso!');
    } else {
      await api.post('/organizer-game-settings', formData);
      message.success('Dia de jogo criado com sucesso!');
    }
    setGameSettingModalVisible(false);
    fetchGameSettings();
  };

  const handleGameSettingSubmit = async (values) => {
    try {
      const formData = { ...values };
      if (formData.time) formData.time = formData.time.format('HH:mm');
      if (formData.early_time_at) formData.early_time_at = formData.early_time_at.format('HH:mm');
      if (formData.first_time_at) formData.first_time_at = formData.first_time_at.format('HH:mm');
      if (formData.second_time_at) formData.second_time_at = formData.second_time_at.format('HH:mm');
      if (formData.third_time_at) formData.third_time_at = formData.third_time_at.format('HH:mm');

      // Se for edicao, checa quantos jogos abertos futuros serao recalculados.
      // Se >0 abre modal de confirmacao explicando o que muda.
      if (editingGameSetting) {
        let affected = 0;
        try {
          const r = await api.get('/organizer-game-settings/games-affected');
          affected = r?.data?.count || 0;
        } catch { /* segue sem preview */ }

        if (affected > 0) {
          Modal.confirm({
            title: 'Atualizar configuração de jogo?',
            content: (
              <div>
                <p><b>{affected} jogo{affected > 1 ? 's' : ''} aberto{affected > 1 ? 's' : ''}</b> {affected > 1 ? 'serão' : 'será'} sincronizado{affected > 1 ? 's' : ''} com as novas horas de convocação.</p>
                <p style={{ marginBottom: 0 }}>As datas das 1ª, 2ª e 3ª chamadas serão recalculadas automaticamente pra bater com esta config nova.</p>
              </div>
            ),
            okText: 'Salvar e atualizar jogos',
            cancelText: 'Cancelar',
            onOk: () => doGameSettingSave(formData),
          });
          return;
        }
      }
      await doGameSettingSave(formData);
    } catch (error) {
      message.error('Erro ao salvar dia de jogo');
    }
  };

  const handleDeleteGameSetting = async (id) => {
    try {
      await api.delete(`/organizer-game-settings/${id}`);
      message.success('Dia de jogo excluído com sucesso!');
      fetchGameSettings();
    } catch (error) {
      message.error('Erro ao excluir dia de jogo');
    }
  };

  const handleCopyPublicRegisterLink = async (link) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = link;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      message.success('Link de cadastro copiado!');
    } catch (error) {
      message.error('Não foi possível copiar o link');
    }
  };

  const dayOfWeekOptions = [
    { value: 1, label: 'Segunda-feira' },
    { value: 2, label: 'Terça-feira' },
    { value: 3, label: 'Quarta-feira' },
    { value: 4, label: 'Quinta-feira' },
    { value: 5, label: 'Sexta-feira' },
    { value: 6, label: 'Sábado' },
    { value: 0, label: 'Domingo' }
  ];

  const getDayOfWeekLabel = (day) => {
    const option = dayOfWeekOptions.find(opt => opt.value === day);
    return option ? option.label : day;
  };

  const publicRegisterLink = teamConfig?.public_register_code
    ? `${window.location.origin}/cadastro/jogador/${teamConfig.public_register_code}`
    : '';

  const columns = [
    {
      title: (
        <Space>
          Dia da Semana
          <Popover content="Dia da semana em que o jogo acontece">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Popover>
        </Space>
      ),
      dataIndex: 'day_of_week',
      key: 'day_of_week',
      render: (day) => getDayOfWeekLabel(day)
    },
    {
      title: (
        <Space>
          Horário
          <Popover content="Horário de início do jogo">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Popover>
        </Space>
      ),
      dataIndex: 'time',
      key: 'time'
    },
    {
      title: (
        <Space>
          1ª Conv. Dias
          <Popover content="Quantos dias antes do jogo enviar a primeira convocação">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Popover>
        </Space>
      ),
      dataIndex: 'early_call_at',
      key: 'early_call_at'
    },
    {
      title: (
        <Space>
          1ª Conv. Horário
          <Popover content="Horário para enviar a primeira convocação">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Popover>
        </Space>
      ),
      dataIndex: 'early_time_at',
      key: 'early_time_at'
    },
    {
      title: (
        <Space>
          2ª Conv. Dias
          <Popover content="Quantos dias antes do jogo enviar a segunda convocação">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Popover>
        </Space>
      ),
      dataIndex: 'first_call_at',
      key: 'first_call_at'
    },
    {
      title: (
        <Space>
          2ª Conv. Horário
          <Popover content="Horário para enviar a segunda convocação">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Popover>
        </Space>
      ),
      dataIndex: 'first_time_at',
      key: 'first_time_at'
    },
    {
      title: (
        <Space>
          3ª Conv. Dias
          <Popover content="Quantos dias antes do jogo enviar a terceira convocação">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Popover>
        </Space>
      ),
      dataIndex: 'second_call_at',
      key: 'second_call_at'
    },
    {
      title: (
        <Space>
          3ª Conv. Horário
          <Popover content="Horário para enviar a terceira convocação">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Popover>
        </Space>
      ),
      dataIndex: 'second_time_at',
      key: 'second_time_at'
    },
    {
      title: (
        <Space>
          4ª Conv. Dias
          <Popover content="Quantos dias antes do jogo enviar a quarta convocação">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Popover>
        </Space>
      ),
      dataIndex: 'third_call_at',
      key: 'third_call_at'
    },
    {
      title: (
        <Space>
          4ª Conv. Horário
          <Popover content="Horário para enviar a quarta convocação">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Popover>
        </Space>
      ),
      dataIndex: 'third_time_at',
      key: 'third_time_at'
    },
    {
      title: (
        <Space>
          Ações
        </Space>
      ),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => showGameSettingModal(record)} size="small" />
          <Popconfirm title="Excluir este dia de jogo?" onConfirm={() => handleDeleteGameSetting(record.id)}>
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <OrganizerHero />
      <div style={{
        display: 'flex',
        flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: window.innerWidth <= 768 ? 'stretch' : 'center',
        marginBottom: 16,
        gap: window.innerWidth <= 768 ? 12 : 0
      }}>
        <Title level={2} style={{ margin: 0 }}>Configuração</Title>
        <Button type="primary" icon={<EditOutlined />} onClick={showModal}>
          Editar
        </Button>
      </div>

      <Card loading={loading}>
        {teamConfig ? (
          <Descriptions 
            bordered 
            column={window.innerWidth <= 768 ? 1 : 2} 
            labelStyle={{ width: window.innerWidth <= 768 ? '100px' : '150px' }}
            size={window.innerWidth <= 768 ? 'small' : 'default'}
          >
            <Descriptions.Item label="Nome do Time">{teamConfig.game_name}</Descriptions.Item>
            <Descriptions.Item label="Status">{teamConfig.status}</Descriptions.Item>
            <Descriptions.Item label="Jogadores">{teamConfig.players}</Descriptions.Item>
            <Descriptions.Item label="Goleiros">{teamConfig.goalkeepers}</Descriptions.Item>
            <Descriptions.Item label="Texto da Lista" span={2}>
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
                {teamConfig.list_description ? teamConfig.list_description.replace(/\\n/g, '\n') : 'Não definido'}
              </pre>
            </Descriptions.Item>
            <Descriptions.Item label="Registro Público">
              {teamConfig.public_register ? 'Sim' : 'Não'}
            </Descriptions.Item>
            {teamConfig.public_register && teamConfig.public_register_code && (
              <Descriptions.Item label="Link de Cadastro" span={2}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    border: '1px solid #d9d9d9',
                    borderRadius: 8,
                    padding: '8px 10px',
                    background: '#fafafa'
                  }}
                >
                  <span
                    style={{ wordBreak: 'break-all', color: '#595959', flex: 1, minWidth: 0, cursor: 'text' }}
                    onClick={(event) => {
                      const range = document.createRange();
                      range.selectNodeContents(event.currentTarget);
                      const sel = window.getSelection();
                      sel.removeAllRanges();
                      sel.addRange(range);
                    }}
                  >
                    {publicRegisterLink}
                  </span>
                  <Tooltip title="Copiar link de cadastro">
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => handleCopyPublicRegisterLink(publicRegisterLink)}
                      aria-label="Copiar link de cadastro"
                    />
                  </Tooltip>
                </div>
                <div style={{ marginTop: 6, color: '#8c8c8c', fontSize: 12, textAlign: 'right' }}>
                  Clique no ícone para copiar o link.
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          <p>Nenhuma configuração encontrada.</p>
        )}
      </Card>

      <Card style={{ marginTop: 24 }} title="Grupo do WhatsApp">
        {teamConfig?.groupjid ? (
          <Alert
            type="success"
            showIcon
            message="Sincronizado"
            description={
              <div>
                <div style={{ marginBottom: 8 }}>
                  ID do grupo:{' '}
                  <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>
                    {teamConfig.groupjid}
                  </code>
                </div>
                <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                  Trocou de grupo? Ressincronize para apontar o Futezap para o grupo novo.
                </div>
              </div>
            }
            action={
              <Button danger icon={<SyncOutlined />} onClick={handleResync}>
                Ressincronizar grupo
              </Button>
            }
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            message="Não sincronizado"
            description="Você precisa sincronizar o Futezap com seu grupo do WhatsApp para começar a receber as confirmações."
            action={
              <Button type="primary" icon={<SyncOutlined />} onClick={handleResync}>
                Sincronizar agora
              </Button>
            }
          />
        )}
      </Card>

      <Card style={{ marginTop: 24 }} title="Estatísticas do time">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Ativar estatísticas dos jogadores</div>
            <div style={{ color: '#8c8c8c', fontSize: 13 }}>
              Quando ativa, os jogadores conseguem se avaliar após as partidas e o Futezap envia automaticamente
              o link de avaliação no grupo 2h depois do jogo. Frequência, gols, cartões e MVP também
              ficam visíveis no perfil de cada jogador.
            </div>
          </div>
          <Switch
            checked={features.stats_enabled}
            loading={featuresLoading}
            onChange={toggleStats}
            checkedChildren="Sim"
            unCheckedChildren="Não"
          />
        </div>
      </Card>

      <div style={{ marginTop: 24 }}>
        <div style={{
          display: 'flex',
          flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: window.innerWidth <= 768 ? 'stretch' : 'center',
          marginBottom: 16,
          gap: window.innerWidth <= 768 ? 12 : 0
        }}>
          <Title level={3} style={{ margin: 0 }}>Dias de Jogos</Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showGameSettingModal()}>
            Adicionar Dia
          </Button>
        </div>
        
        <Table
          columns={columns}
          dataSource={gameSettings}
          rowKey="id"
          loading={gameSettingsLoading}
          pagination={false}
          scroll={{ x: 800 }}
        />
      </div>

      <Modal
        title="Editar Configuração do Time"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item label="Escudo do time" extra="PNG, JPG ou WebP. Máx 2MB. Aparece no mapa e no perfil do time.">
            <Space>
              {logoUrl
                ? <Avatar size={64} src={absoluteUploadUrl(logoUrl)} shape="square" />
                : <Avatar size={64} icon={<TeamOutlined />} shape="square" />}
              <Upload
                accept="image/png,image/jpeg,image/webp"
                showUploadList={false}
                customRequest={async ({ file, onSuccess, onError }) => {
                  setUploadingLogo(true);
                  try {
                    const fd = new FormData();
                    fd.append('image', file);
                    const r = await api.post('/team-logo/upload', fd, {
                      headers: { 'Content-Type': 'multipart/form-data' },
                    });
                    setLogoUrl(r.data.url);
                    form.setFieldsValue({ logo_url: r.data.url });
                    onSuccess(r.data);
                    message.success('Escudo enviado');
                  } catch (err) {
                    onError(err);
                    message.error(err?.response?.data?.message || 'Erro ao enviar');
                  } finally { setUploadingLogo(false); }
                }}
              >
                <Button icon={uploadingLogo ? <LoadingOutlined /> : <UploadOutlined />} loading={uploadingLogo}>
                  {logoUrl ? 'Trocar escudo' : 'Enviar escudo'}
                </Button>
              </Upload>
            </Space>
          </Form.Item>
          <Form.Item name="logo_url" hidden><Input /></Form.Item>
          <Form.Item name="game_name" label="Nome do Time" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="players" label="Jogadores" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="goalkeepers" label="Goleiros" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="list_description"
            label="Texto a ser enviado na lista de convocação"
            help="Texto adicional que será incluído nas mensagens de convocação."
          >
            <Input.TextArea
              rows={4}
              placeholder="Ex: Local: Campo do João\nHorário: 19h\nValor: R$ 10,00"
            />
          </Form.Item>
          <Form.Item name="public_register" label="Registro Público" valuePropName="checked">
            <Switch checkedChildren="Sim" unCheckedChildren="Não" />
          </Form.Item>
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="Seções da lista enviada no grupo"
            description="Escolha o que aparece na lista que vai pro WhatsApp. Times com muita gente podem esconder 'Lista de espera' e 'Não confirmados' pra deixar a msg mais enxuta." />
          <Form.Item name="show_waiting_list" label="Mostrar lista de espera"
            valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Sim" unCheckedChildren="Não" />
          </Form.Item>
          <Form.Item name="show_not_confirmed_players" label="Mostrar jogadores não confirmados"
            valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Sim" unCheckedChildren="Não" />
          </Form.Item>

          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="Quem recebe cada chamada"
            description="Define pra quem cada convocação vai. Mensalista: jogadores fixos do time. Avulso: todos (mensalistas + convidados). O aviso prévio sempre vai só pros mensalistas — regra do sistema." />
          <Form.Item name="first_call_player_type" label="1ª chamada — enviar para:"
            initialValue={2}>
            <Select>
              <Option value={1}>Só Mensalistas</Option>
              <Option value={2}>Todos (Mensalistas + Avulsos)</Option>
            </Select>
          </Form.Item>
          <Form.Item name="second_call_player_type" label="2ª chamada — enviar para:"
            initialValue={2}>
            <Select>
              <Option value={1}>Só Mensalistas</Option>
              <Option value={2}>Todos (Mensalistas + Avulsos)</Option>
            </Select>
          </Form.Item>
          <Form.Item name="third_call_player_type" label="3ª chamada — enviar para:"
            initialValue={2}>
            <Select>
              <Option value={1}>Só Mensalistas</Option>
              <Option value={2}>Todos (Mensalistas + Avulsos)</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalVisible(false)}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit">
                Salvar
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingGameSetting ? 'Editar Dia de Jogo' : 'Novo Dia de Jogo'}
        open={gameSettingModalVisible}
        onCancel={() => setGameSettingModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form form={gameSettingForm} onFinish={handleGameSettingSubmit} layout="vertical">
          <Form.Item name="day_of_week" label="Dia da Semana" rules={[{ required: true }]}>
            <Select>
              {dayOfWeekOptions.map(option => (
                <Option key={option.value} value={option.value}>{option.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="time" label="Horário do Jogo" rules={[{ required: true }]}>
            <TimePicker format="HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          
          <Typography.Title level={5}>1ª Convocação</Typography.Title>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="early_call_at" label="Dias Antes" style={{ flex: 1 }}>
              <InputNumber min={0} max={30} />
            </Form.Item>
            <Form.Item name="early_time_at" label="Horário" style={{ flex: 1 }}>
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item 
              name="early_confirmation_add_extra_time" 
              label={
                <Space>
                  Envio Antecipado (h)
                  <Popover 
                    content="Quantas horas antes do horário de confirmação a lista deve ser enviada. Exemplo: se a confirmação libera às 8h e você definir 2h, a lista será enviada às 6h para que todos recebam antes da liberação."
                    title="Envio Antecipado da Lista"
                  >
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Popover>
                </Space>
              } 
              style={{ flex: 1 }}
            >
              <InputNumber min={0} max={24} />
            </Form.Item>
          </div>
          
          <Typography.Title level={5}>2ª Convocação</Typography.Title>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="first_call_at" label="Dias Antes" style={{ flex: 1 }}>
              <InputNumber min={0} max={30} />
            </Form.Item>
            <Form.Item name="first_time_at" label="Horário" style={{ flex: 1 }}>
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
            <div style={{ flex: 1 }}></div>
          </div>
          
          <Typography.Title level={5}>3ª Convocação</Typography.Title>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="second_call_at" label="Dias Antes" style={{ flex: 1 }}>
              <InputNumber min={0} max={30} />
            </Form.Item>
            <Form.Item name="second_time_at" label="Horário" style={{ flex: 1 }}>
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
            <div style={{ flex: 1 }}></div>
          </div>
          
          <Typography.Title level={5}>4ª Convocação</Typography.Title>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="third_call_at" label="Dias Antes" style={{ flex: 1 }}>
              <InputNumber min={0} max={30} />
            </Form.Item>
            <Form.Item name="third_time_at" label="Horário" style={{ flex: 1 }}>
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
            <div style={{ flex: 1 }}></div>
          </div>
          
          <Form.Item>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => setGameSettingModalVisible(false)}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit">
                Salvar
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TeamConfig;